/**
 * usePlotComparison — detects when saved plot snapshot data is stale
 * relative to the current reference quantities, and provides old/new/both/history
 * view modes with recomputed data.
 *
 * Recomputation: CL_new = CL_f360 * ref_area_f360 / wing_area
 *
 * The snapshot stores CL/CD as computed with the *original* wing_area
 * (snapshotRefArea in config). When the live wing_area differs, the hook
 * recomputes all correctable coefficients.
 *
 * "history" mode overlays N historical S_ref values on the same chart.
 */

import { useCallback, useMemo, useState } from "react";
import type { SavedPlot } from "../api/plots";
import type { ReferenceQuantity, RefQuantityHistoryEntry } from "../api/referenceQuantities";
import { useReferenceQuantities, useRefQuantityHistory } from "./useReferenceQuantities";
import { useRefAreaPreview } from "../context/RefAreaPreviewContext";

export type ComparisonViewMode = "old" | "new" | "both" | "history";

const CORRECTABLE_COEFFICIENTS = [
  "CL",
  "CD",
  "CX",
  "CY",
  "CZ",
  "CFx",
  "CFy",
  "CFz",
  "CM_pitching",
];

export interface PlotComparisonResult {
  hasChanges: boolean;
  viewMode: ComparisonViewMode;
  setViewMode: (mode: ComparisonViewMode) => void;
  displayData: Record<string, unknown>[];
  displayConfig: Record<string, unknown>;
  snapshotRefArea: number | null;
  liveRefArea: number | null;
  refAreaUnits: string | null;
  history: RefQuantityHistoryEntry[];
  selectedHistoryValues: Set<number>;
  toggleHistoryValue: (value: number) => void;
}

export function extractWingArea(
  refQuantities: ReferenceQuantity[] | undefined,
): { value: number | null; units: string | null; nodeId: string | null } {
  if (!refQuantities) return { value: null, units: null, nodeId: null };
  const wa = refQuantities.find((rq) => rq.parameter === "wing_area");
  return {
    value: wa?.value ?? null,
    units: wa?.units ?? null,
    nodeId: wa?.id ?? null,
  };
}

function recomputeRow(
  row: Record<string, unknown>,
  newWingArea: number,
): Record<string, unknown> {
  const refAreaF360 = Number(row.ref_area_f360);
  if (!refAreaF360 || !isFinite(refAreaF360)) return { ...row };

  const ratio = refAreaF360 / newWingArea;
  const out = { ...row };
  for (const coeff of CORRECTABLE_COEFFICIENTS) {
    const baseVal = row[`${coeff}_f360`] ?? row[coeff];
    if (baseVal != null && typeof baseVal === "number" && isFinite(baseVal)) {
      out[coeff] = baseVal * ratio;
    }
  }
  return out;
}

export function recomputeSnapshot(
  snapshot: Record<string, unknown>[],
  newWingArea: number,
): Record<string, unknown>[] {
  return snapshot.map((row) => recomputeRow(row, newWingArea));
}

function deduplicateHistoryValues(entries: RefQuantityHistoryEntry[]): RefQuantityHistoryEntry[] {
  const seen = new Set<number | null>();
  return entries.filter((e) => {
    if (seen.has(e.value)) return false;
    seen.add(e.value);
    return true;
  });
}

export function usePlotComparison(
  plot: SavedPlot,
  projectId: string,
  branch: string,
): PlotComparisonResult {
  const { data: refQuantities } = useReferenceQuantities(projectId, branch);
  const { previewRefArea } = useRefAreaPreview();

  const rawConfig = plot.config as Record<string, unknown>;
  const snapshotRefArea =
    typeof rawConfig.snapshotRefArea === "number"
      ? rawConfig.snapshotRefArea
      : null;
  const snapshotRefAreaUnits =
    typeof rawConfig.snapshotRefAreaUnits === "string"
      ? rawConfig.snapshotRefAreaUnits
      : null;

  const { value: serverRefArea, units: liveRefAreaUnits, nodeId: wingAreaNodeId } =
    extractWingArea(refQuantities);
  const liveRefArea = previewRefArea ?? serverRefArea;

  const { data: rawHistory } = useRefQuantityHistory(
    wingAreaNodeId ?? undefined,
    projectId,
    branch,
  );
  const history = useMemo(
    () => deduplicateHistoryValues(rawHistory ?? []),
    [rawHistory],
  );

  const hasChanges =
    snapshotRefArea !== null &&
    liveRefArea !== null &&
    Math.abs(snapshotRefArea - liveRefArea) > 1e-9;

  const [viewMode, setViewMode] = useState<ComparisonViewMode>("new");
  const [selectedHistoryValues, setSelectedHistoryValues] = useState<Set<number>>(() => new Set());

  const toggleHistoryValue = useCallback((value: number) => {
    setSelectedHistoryValues((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const oldData = useMemo(
    () => (Array.isArray(plot.data_snapshot) ? plot.data_snapshot : []) as Record<string, unknown>[],
    [plot.data_snapshot],
  );

  const newData = useMemo(() => {
    if (!hasChanges || liveRefArea === null) return oldData;
    return recomputeSnapshot(oldData, liveRefArea);
  }, [oldData, hasChanges, liveRefArea]);

  const refAreaUnits = snapshotRefAreaUnits ?? liveRefAreaUnits;

  const { displayData, displayConfig } = useMemo(() => {
    if (!hasChanges && viewMode !== "history") {
      return { displayData: oldData, displayConfig: rawConfig };
    }

    const units = refAreaUnits ? ` ${refAreaUnits}` : "";
    const oldLabel = `S_ref = ${snapshotRefArea}${units}`;
    const newLabel = `S_ref = ${liveRefArea}${units}`;

    switch (viewMode) {
      case "old":
        return { displayData: oldData, displayConfig: rawConfig };
      case "new":
        return { displayData: newData, displayConfig: rawConfig };
      case "both": {
        const merged = [
          ...oldData.map((r) => ({ ...r, _comparison: oldLabel })),
          ...newData.map((r) => ({ ...r, _comparison: newLabel })),
        ];
        const bothConfig = {
          ...rawConfig,
          groupBy: "_comparison",
          showLegend: true,
        };
        return { displayData: merged, displayConfig: bothConfig };
      }
      case "history": {
        const valuesToShow =
          selectedHistoryValues.size > 0
            ? [...selectedHistoryValues]
            : history
                .map((h) => h.value)
                .filter((v): v is number => v != null);

        if (valuesToShow.length === 0) {
          return { displayData: oldData, displayConfig: rawConfig };
        }

        const merged: Record<string, unknown>[] = [];
        for (const val of valuesToShow) {
          const label = `S_ref = ${val}${units}`;
          const recomputed = recomputeSnapshot(oldData, val);
          for (const row of recomputed) {
            merged.push({ ...row, _comparison: label });
          }
        }
        const historyConfig = {
          ...rawConfig,
          groupBy: "_comparison",
          showLegend: true,
        };
        return { displayData: merged, displayConfig: historyConfig };
      }
    }
  }, [
    hasChanges,
    viewMode,
    oldData,
    newData,
    rawConfig,
    snapshotRefArea,
    liveRefArea,
    refAreaUnits,
    selectedHistoryValues,
    history,
  ]);

  return {
    hasChanges,
    viewMode: hasChanges || viewMode === "history" ? viewMode : "old",
    setViewMode,
    displayData,
    displayConfig,
    snapshotRefArea,
    liveRefArea,
    refAreaUnits,
    history,
    selectedHistoryValues,
    toggleHistoryValue,
  };
}
